<template>
  <FilterBadge v-bind="{ ...$props, ...$attrs }" icon="fas fa-flag">
    {{ text }}
  </FilterBadge>
</template>

<script setup lang="ts">
import { gql } from "@apollo/client/core";
import { computed, onMounted, ref, watch, type PropType } from "vue";
import { useTranslation } from "@/language/translation";
import { apolloClient } from "@/graphql/apollo";
import apolloCache from "@/graphql/apolloCache";
import FlagsFilter from "../../filterImplementations/FlagsFilter";
import FilterBadge from "./FilterBadge.vue";
import joinList from "./joinList";

const props = defineProps({
  filter: {
    type: Object as PropType<FlagsFilter>,
    required: true,
  },
});

const { t, te } = useTranslation();
const loadedFlags = ref<
  Record<
    number,
    {
      displayNameId: number;
      abbreviationId: number;
      id: number;
      name: string;
    }
  >
>({});

function readCachedFlag(flagId: number) {
  return apolloCache.readFragment<{
    displayNameId: number;
    abbreviationId: number;
    id: number;
    name: string;
  }>({
    id: `Flag:${flagId}`,
    fragment: gql`
      fragment SQDCFilterBadgeFlagName on Flag {
        displayNameId
        abbreviationId
        id
        name
      }
    `,
  });
}

async function loadMissingFlags() {
  const missingFlagIds = props.filter.flagIds.filter(
    (flagId) => readCachedFlag(flagId) == null && loadedFlags.value[flagId] == null,
  );
  if (missingFlagIds.length === 0) {
    return;
  }

  const { data } = await apolloClient.query<{
    Flags: {
      displayNameId: number;
      abbreviationId: number;
      id: number;
      name: string;
    }[];
  }>({
    query: gql`
      query SQDCFilterBadgeFlags($ids: [Int!]!) {
        Flags(ids: $ids) {
          displayNameId
          abbreviationId
          id
          name
        }
      }
    `,
    variables: {
      ids: missingFlagIds,
    },
    fetchPolicy: "cache-first",
  });

  loadedFlags.value = {
    ...loadedFlags.value,
    ...Object.fromEntries(data.Flags.map((flag) => [flag.id, flag])),
  };
}

const text = computed(() => {
  const filterNames = props.filter.flagIds.map((flagId) => {
    const { displayNameId, abbreviationId, name, id } = readCachedFlag(flagId) ??
      loadedFlags.value[flagId] ?? { displayNameId: null, id: flagId };
    return te(`dynamic.${displayNameId}`)
      ? t(`dynamic.${displayNameId}`)
      : te(`dynamic.${abbreviationId}`)
        ? t(`dynamic.${abbreviationId}`)
        : (name ?? `[${t("global.id")}:${id}]`);
  });

  return filterNames.length === 0
    ? t("components.event-action.filter.ui.badges.FlagsFilterBadge.withoutFlags")
    : joinList(filterNames);
});

onMounted(loadMissingFlags);
watch(() => props.filter.flagIds, loadMissingFlags);
</script>

<style lang="scss" scoped></style>
